// src/components/ui/NotificationContainer.jsx
import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import notificationService from '@/services/notificationService';

// ==================== ID GENERATOR ====================

let idCounter = 0;
const generateUniqueId = () =>
  `notification-${Date.now()}-${++idCounter}-${Math.random().toString(36).substr(2, 9)}`;

// ==================== CONTEXTO COMPARTILHADO ====================

/**
 * Contexto global de notificações.
 * Garante que GlobalNotificationContainer, useNotification e qualquer
 * consumidor compartilhem o mesmo estado — eliminando o bug de instâncias
 * isoladas que nunca recebiam notificações externas (BUG #3).
 *
 * Uso:
 *   1. Envolver a árvore com <NotificationProvider>
 *   2. Usar useNotification() em qualquer componente filho para disparar toasts
 *   3. Renderizar <GlobalNotificationContainer /> uma única vez no layout raiz
 */
const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const hook = useNotificationState();

  return (
    <NotificationContext.Provider value={hook}>
      {children}
    </NotificationContext.Provider>
  );
};

// ==================== COMPONENTE VISUAL ====================

/**
 * Renderiza a lista de notificações visíveis.
 * Recebe `notifications` via prop — não gerencia estado próprio de lista,
 * apenas cuida da animação de entrada/saída e dos timers de auto-remoção.
 */
export const NotificationContainer = ({ notifications = [], position = 'top-right' }) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);
  const [animatingOut, setAnimatingOut] = useState(new Set());
  const timersRef = useRef(new Map());

  // IDs já adicionados à lista visual. NÃO são removidos aqui quando a
  // notificação sai — só são limpos no desmonte do container.
  // Isso impede que uma notificação ainda presente na prop `notifications`
  // seja re-adicionada após ser fechada pelo usuário (BUG #5).
  const processedIdsRef = useRef(new Set());

  // Cleanup completo ao desmontar
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
      processedIdsRef.current.clear();
    };
  }, []);

  // Sincronizar novas notificações vindas da prop
  useEffect(() => {
    const newNotifications = notifications.filter(
      n => n.id && !processedIdsRef.current.has(n.id)
    );

    if (newNotifications.length === 0) return;

    newNotifications.forEach(n => processedIdsRef.current.add(n.id));
    setVisibleNotifications(prev => [...prev, ...newNotifications]);
  }, [notifications]); // visibleNotifications intencionalmente fora das deps

  const removeNotification = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // CORREÇÃO BUG #5: NÃO deletar de processedIdsRef aqui.
    // Se o pai ainda mantiver a notificação no array, ela não será re-adicionada.

    setAnimatingOut(prev => new Set([...prev, id]));

    const removeTimer = setTimeout(() => {
      setVisibleNotifications(prev => prev.filter(n => n.id !== id));
      setAnimatingOut(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);

    // Timer de animação curto — não precisa ser rastreado para cleanup
    // pois o componente vive enquanto há notificações visíveis
    void removeTimer;
  }, []);

  // Auto-remoção por tempo — dependência estável via join de IDs
  useEffect(() => {
    visibleNotifications.forEach(notification => {
      if (notification.duration !== 0 && !timersRef.current.has(notification.id)) {
        const duration = notification.duration || 5000;
        const timer = setTimeout(() => {
          removeNotification(notification.id);
        }, duration);
        timersRef.current.set(notification.id, timer);
      }
    });
  }, [visibleNotifications.map(n => n.id).join(','), removeNotification]);

  const getPositionClasses = (pos) => {
    const positions = {
      'top-right':     'top-4 right-4',
      'top-left':      'top-4 left-4',
      'top-center':    'top-4 left-1/2 transform -translate-x-1/2',
      'bottom-right':  'bottom-4 right-4',
      'bottom-left':   'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
    };
    return positions[pos] || positions['top-right'];
  };

  const getNotificationStyles = (type) => {
    const styles = {
      success: { bg: 'bg-green-50 border-green-200',  text: 'text-green-800',  icon: '✅', iconBg: 'bg-green-100 text-green-600' },
      error:   { bg: 'bg-red-50 border-red-200',      text: 'text-red-800',    icon: '❌', iconBg: 'bg-red-100 text-red-600' },
      warning: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-800', icon: '⚠️', iconBg: 'bg-yellow-100 text-yellow-600' },
      info:    { bg: 'bg-blue-50 border-blue-200',    text: 'text-blue-800',   icon: 'ℹ️', iconBg: 'bg-blue-100 text-blue-600' },
      loading: { bg: 'bg-gray-50 border-gray-200',    text: 'text-gray-800',   icon: '⏳', iconBg: 'bg-gray-100 text-gray-600' }
    };
    return styles[type] || styles.info;
  };

  const NotificationItem = ({ notification, onRemove, isAnimatingOut }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [progress, setProgress] = useState(100);
    const progressIntervalRef = useRef(null);
    const styles = getNotificationStyles(notification.type);

    useEffect(() => {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
      if (notification.duration && notification.duration > 0) {
        const startTime = notification.timestamp || Date.now();

        progressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, notification.duration - elapsed);
          setProgress((remaining / notification.duration) * 100);
          if (remaining <= 0) clearInterval(progressIntervalRef.current);
        }, 100);

        return () => {
          if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
      }
    }, [notification.duration, notification.timestamp]);

    const handleClick = () => {
      if (notification.onClick) notification.onClick();
      onRemove(notification.id);
    };

    return (
      <div
        className={`
          relative w-full max-w-sm mx-auto mb-3 transform transition-all duration-300 ease-in-out
          ${isVisible && !isAnimatingOut
            ? 'translate-x-0 opacity-100 scale-100'
            : 'translate-x-full opacity-0 scale-95'
          }
          ${notification.onClick ? 'cursor-pointer hover:scale-105' : ''}
        `}
        onClick={notification.onClick ? handleClick : undefined}
      >
        <div className={`relative rounded-lg border shadow-lg p-4 ${styles.bg} ${styles.text} hover:shadow-xl transition-shadow duration-200`}>
          <div className="flex items-start">
            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${styles.iconBg}`}>
              <span className="text-sm">{styles.icon}</span>
            </div>

            <div className="flex-1 min-w-0">
              {notification.title && (
                <h4 className="text-sm font-semibold mb-1 truncate">{notification.title}</h4>
              )}
              <p className="text-sm leading-relaxed">{notification.message}</p>

              {notification.actions && (
                <div className="flex space-x-2 mt-3">
                  {notification.actions.map((action, index) => (
                    <button
                      key={index}
                      onClick={(e) => { e.stopPropagation(); action.onClick(); onRemove(notification.id); }}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        action.primary
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {!notification.persistent && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(notification.id); }}
                className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar notificação"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {notification.duration > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
              <div
                className="h-full bg-current opacity-30 transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (visibleNotifications.length === 0) return null;

  return createPortal(
    <div
      className={`fixed z-50 pointer-events-none ${getPositionClasses(position)}`}
      style={{ maxWidth: '400px' }}
    >
      <div className="pointer-events-auto">
        {visibleNotifications.map((notification, index) => (
          <NotificationItem
            key={`${notification.id}-${notification.timestamp || index}`}
            notification={notification}
            onRemove={removeNotification}
            isAnimatingOut={animatingOut.has(notification.id)}
          />
        ))}
      </div>
    </div>,
    document.body
  );
};

// ==================== ESTADO INTERNO DO HOOK ====================

/**
 * Lógica pura de estado — sem efeitos colaterais de Realtime.
 * Usado tanto por useNotification() (via contexto) quanto
 * por useSystemNotifications() (que adiciona a camada Realtime).
 */
const useNotificationState = () => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  const addNotification = useCallback((notification) => {
    const id = notification.id || generateUniqueId();
    const timestamp = Date.now();

    const newNotification = {
      duration: 5000,
      type: 'info',
      ...notification,
      id,
      timestamp
    };

    setNotifications(prev => {
      const withoutDuplicate = prev.filter(n => n.id !== id);
      return [...withoutDuplicate, newNotification];
    });

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((message, options = {}) =>
    addNotification({ type: 'success', message, ...options }), [addNotification]);

  const showError = useCallback((message, options = {}) =>
    addNotification({ type: 'error', message, duration: 8000, ...options }), [addNotification]);

  const showWarning = useCallback((message, options = {}) =>
    addNotification({ type: 'warning', message, ...options }), [addNotification]);

  const showInfo = useCallback((message, options = {}) =>
    addNotification({ type: 'info', message, ...options }), [addNotification]);

  const showLoading = useCallback((message, options = {}) =>
    addNotification({ type: 'loading', message, duration: 0, persistent: true, ...options }), [addNotification]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading
  };
};

// ==================== HOOK PÚBLICO ====================

/**
 * Hook para disparar notificações a partir de qualquer componente.
 *
 * Se estiver dentro de um <NotificationProvider>, usa o contexto
 * compartilhado (recomendado). Caso contrário, cria estado local
 * — útil apenas em testes ou componentes totalmente isolados.
 */
export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return ctx ?? useNotificationState();
};

// ==================== HOOK COM REALTIME ====================

/**
 * Estende useNotification com escuta de notificações do Supabase Realtime.
 *
 * CORREÇÃO BUG #2: startListening não depende mais de `isListening` state.
 * O controle de "já inscrito" é feito via channelRef (ref estável), evitando
 * que o useCallback seja recriado e reative o useEffect do consumidor.
 */
export const useSystemNotifications = () => {
  const notificationHook = useNotification();
  const channelRef = useRef(null); // ref estável — não causa re-renders

  const startListening = useCallback((userId) => {
    // Usar ref em vez de state para checar se já está inscrito
    if (channelRef.current || !userId) return;

    channelRef.current = notificationService.subscribeToNotifications(
      userId,
      (newNotification) => {
        notificationHook.addNotification({
          id:       newNotification.id,
          type:     newNotification.type || 'info',
          title:    newNotification.title,
          message:  newNotification.message,
          duration: 5000
        });
      }
    );
  // addNotification é estável (useCallback sem deps variáveis)
  }, [notificationHook.addNotification]);

  const stopListening = useCallback(async () => {
    if (channelRef.current) {
      await notificationService.unsubscribe(channelRef.current);
      channelRef.current = null;
    }
  }, []); // sem deps — só usa a ref

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    ...notificationHook,
    startListening,
    stopListening,
    isListening: () => channelRef.current !== null
  };
};

// ==================== COMPONENTES INTEGRADOS ====================

/**
 * Container que combina escuta Realtime + renderização visual.
 * Renderizar apenas uma vez na árvore (ex: dentro do DashboardLayout).
 */
export const SystemNotificationContainer = ({ userId, position = 'top-right' }) => {
  const { notifications, startListening, stopListening } = useSystemNotifications();

  useEffect(() => {
    if (userId) {
      startListening(userId);
    } else {
      stopListening();
    }
    return () => { stopListening(); };
  // startListening e stopListening são estáveis — não reativam o efeito
  }, [userId, startListening, stopListening]);

  return <NotificationContainer notifications={notifications} position={position} />;
};

/**
 * Container global que consome o NotificationContext compartilhado.
 *
 * CORREÇÃO BUG #3: não instancia mais um useNotification() local.
 * Lê do contexto, garantindo que chamadas a showSuccess/showError em
 * qualquer parte da app sejam refletidas aqui.
 *
 * Pré-requisito: <NotificationProvider> deve envolver esta árvore.
 */
export const GlobalNotificationContainer = ({ position = 'top-right' }) => {
  const ctx = useContext(NotificationContext);

  if (!ctx) {
    console.warn(
      '[GlobalNotificationContainer] Nenhum NotificationProvider encontrado na árvore. ' +
      'Envolva a aplicação com <NotificationProvider> para que este container funcione.'
    );
    return null;
  }

  return <NotificationContainer notifications={ctx.notifications} position={position} />;
};

export default NotificationContainer;
